import React, { useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'
import { getPublicCertificate } from '../../../api/certificate'
import {
  CertificateConfig,
  DEFAULT_CERTIFICATE_CBT_TEMPLATE,
  DEFAULT_CERTIFICATE_TEMPLATE,
  downloadGeneratedCertificate,
  getCertificateAsBase64,
} from '../../../utils/certificateGenerator'
import { all_routes } from '../../router/all_routes'

const romanMonths = [
  'I',
  'II',
  'III',
  'IV',
  'V',
  'VI',
  'VII',
  'VIII',
  'IX',
  'X',
  'XI',
  'XII',
]

type MaterialRow = {
  topic: string
  jpl: string
  speaker: string
}

type EvaluationOverride = 'auto' | 'passed' | 'failed'

const buildCertificateNumber = (sequence: string, isoDate: string) => {
  const date = dayjs(isoDate)
  const monthRoman = romanMonths[date.month()] || 'I'
  const year = date.year()
  const padded = sequence.padStart(3, '0')
  return `${padded}/JP-SF/${monthRoman}/${year}`
}

const CertificateAdminPreview: React.FC = () => {
  const { certificateNumber } = useParams<{ certificateNumber: string }>()
  const { search } = useLocation()
  const navigate = useNavigate()
  const queryNumber = new URLSearchParams(search).get('number') || ''
  const resolvedNumber = certificateNumber || queryNumber

  const [searchNumber, setSearchNumber] = useState(resolvedNumber)
  const [manualCertificateNumber, setManualCertificateNumber] = useState('')
  const [eventDates, setEventDates] = useState('')
  const [jplTotal, setJplTotal] = useState('')
  const [jplNote, setJplNote] = useState('')
  const [speakers, setSpeakers] = useState('')
  const [location, setLocation] = useState('Bandar Lampung')
  const [signatureDateOverride, setSignatureDateOverride] = useState('')
  const [materials, setMaterials] = useState<MaterialRow[]>([])
  const [cbtEnabled, setCbtEnabled] = useState(true)
  const [evaluationScore, setEvaluationScore] = useState('')
  const [evaluationPassingGrade, setEvaluationPassingGrade] = useState('')
  const [evaluationOverride, setEvaluationOverride] =
    useState<EvaluationOverride>('auto')
  const [previewUrl, setPreviewUrl] = useState('')
  const [isPreviewLoading, setIsPreviewLoading] = useState(false)

  const { data, isLoading, isError } = useQuery({
    queryKey: ['admin-certificate', resolvedNumber],
    queryFn: () => getPublicCertificate(resolvedNumber),
    enabled: Boolean(resolvedNumber),
  })

  const certificateData = data?.data
  const course = certificateData?.course
  const cert = certificateData?.certificate

  const defaultSignatureDate = useMemo(() => {
    if (!course || !cert) return ''
    if (course.type === 'Live_Teaching') {
      return course.end_date || ''
    }
    return cert.created_at || ''
  }, [course, cert])

  const signatureDate = signatureDateOverride || defaultSignatureDate
  const autoCertificateNumber = useMemo(() => {
    if (cert?.certificate_number) return cert.certificate_number
    if (!cert?.id || !signatureDate) return ''
    return buildCertificateNumber(String(cert.id), signatureDate)
  }, [cert, signatureDate])

  const effectiveCertificateNumber =
    manualCertificateNumber || autoCertificateNumber

  const qrValue = useMemo(() => {
    if (cert?.qr_url) return cert.qr_url
    if (!effectiveCertificateNumber) return ''
    return `${window.location.origin}${
      all_routes.publicCertificateQuery
    }?number=${encodeURIComponent(effectiveCertificateNumber)}`
  }, [cert, effectiveCertificateNumber])

  const evaluationStatus = useMemo(() => {
    if (!cbtEnabled) return undefined

    if (evaluationOverride === 'passed') return true
    if (evaluationOverride === 'failed') return false

    const score = Number(evaluationScore)
    const passing = Number(evaluationPassingGrade)
    if (!Number.isFinite(score) || !Number.isFinite(passing)) return undefined

    return score >= passing
  }, [cbtEnabled, evaluationOverride, evaluationScore, evaluationPassingGrade])

  useEffect(() => {
    if (!course || !cert) return

    setEventDates(course.certificate_event_dates || '')
    setJplTotal(course.certificate_jpl_total || '')
    setJplNote(course.certificate_jpl_note || '')
    setSpeakers(course.certificate_speakers || '')
    setLocation(course.certificate_location || 'Bandar Lampung')

    const studied = course.studied || []
    setMaterials(
      studied.map((topic) => ({ topic, jpl: '', speaker: '' })),
    )

    setEvaluationScore(cert.evaluation_score || '')
    setEvaluationPassingGrade(
      cert.evaluation_passing_grade || course.min_passing_score || '',
    )
    setCbtEnabled(Boolean(cert.evaluation_score || course.min_passing_score))
  }, [course, cert])

  useEffect(() => {
    let timeoutId: NodeJS.Timeout | null = null

    const generatePreview = async () => {
      if (!course || !cert) return

      const config: CertificateConfig = {
        name: cert.name,
        courseName: course.title,
        y: 260,
        courseY: 360,
        fontSize: 24,
        fontColor: { r: 42, g: 170, b: 250 },
        courseFontSize: 22,
        courseFontColor: { r: 42, g: 170, b: 250 },
        enableTextWrapping: true,
        maxTextWidth: 90,
        lineHeight: 1.3,
        certificateNumber: effectiveCertificateNumber || undefined,
        qrValue: qrValue || undefined,
        eventDates: eventDates || undefined,
        jplTotal: jplTotal || undefined,
        jplNote: jplNote || undefined,
        speakers: speakers || undefined,
        materials:
          materials.length > 0
            ? materials.map((row) => ({
                topic: row.topic,
                jpl: row.jpl,
                speaker: row.speaker,
              }))
            : undefined,
        evaluationScore: cbtEnabled ? evaluationScore || undefined : undefined,
        evaluationPassed: evaluationStatus,
        evaluationPassingGrade: cbtEnabled
          ? evaluationPassingGrade || undefined
          : undefined,
        signature: {
          location: location || 'Bandar Lampung',
          date: signatureDate || undefined,
          organizerText: 'Organized by PT. Jadi Praktisi Indonesia',
          showOrganizerText: false,
          signerName: 'apt. Rudi Afrinanda, S.Farm',
          signerTitle: 'Director',
        },
      }

      const template =
        course.type === 'CBT'
          ? DEFAULT_CERTIFICATE_CBT_TEMPLATE
          : DEFAULT_CERTIFICATE_TEMPLATE

      setIsPreviewLoading(true)
      try {
        const base64 = await getCertificateAsBase64(config, template)
        setPreviewUrl(base64)
      } finally {
        setIsPreviewLoading(false)
      }
    }

    timeoutId = setTimeout(() => {
      generatePreview()
    }, 300)

    return () => {
      if (timeoutId) clearTimeout(timeoutId)
    }
  }, [
    course,
    cert,
    effectiveCertificateNumber,
    qrValue,
    eventDates,
    jplTotal,
    jplNote,
    speakers,
    materials,
    cbtEnabled,
    evaluationScore,
    evaluationPassingGrade,
    evaluationStatus,
    location,
    signatureDate,
  ])

  const handleSearch = () => {
    const trimmedNumber = searchNumber.trim()
    if (!trimmedNumber) return
    navigate(
      `${all_routes.adminCertificates}?number=${encodeURIComponent(
        trimmedNumber,
      )}`,
    )
  }

  const handleAddMaterial = () => {
    setMaterials((prev) => [...prev, { topic: '', jpl: '', speaker: '' }])
  }

  const handleUpdateMaterial = (
    index: number,
    key: keyof MaterialRow,
    value: string,
  ) => {
    setMaterials((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [key]: value }
      return next
    })
  }

  const handleRemoveMaterial = (index: number) => {
    setMaterials((prev) => prev.filter((_, rowIndex) => rowIndex !== index))
  }

  const handleDownload = async () => {
    if (!course || !cert) return

    const config: CertificateConfig = {
      name: cert.name,
      courseName: course.title,
      y: 260,
      courseY: 360,
      fontSize: 24,
      fontColor: { r: 42, g: 170, b: 250 },
      courseFontSize: 22,
      courseFontColor: { r: 42, g: 170, b: 250 },
      enableTextWrapping: true,
      maxTextWidth: 90,
      lineHeight: 1.3,
      certificateNumber: effectiveCertificateNumber || undefined,
      qrValue: qrValue || undefined,
      eventDates: eventDates || undefined,
      jplTotal: jplTotal || undefined,
      jplNote: jplNote || undefined,
      speakers: speakers || undefined,
      materials:
        materials.length > 0
          ? materials.map((row) => ({
              topic: row.topic,
              jpl: row.jpl,
              speaker: row.speaker,
            }))
          : undefined,
      evaluationScore: cbtEnabled ? evaluationScore || undefined : undefined,
      evaluationPassed: evaluationStatus,
      evaluationPassingGrade: cbtEnabled
        ? evaluationPassingGrade || undefined
        : undefined,
      signature: {
        location: location || 'Bandar Lampung',
        date: signatureDate || undefined,
        organizerText: 'Organized by PT. Jadi Praktisi Indonesia',
        showOrganizerText: false,
        signerName: 'apt. Rudi Afrinanda, S.Farm',
        signerTitle: 'Director',
      },
    }

    const template =
      course.type === 'CBT'
        ? DEFAULT_CERTIFICATE_CBT_TEMPLATE
        : DEFAULT_CERTIFICATE_TEMPLATE

    await downloadGeneratedCertificate(config, template)
  }

  return (
    <div className="certificate-admin">
      <style>{`
        .certificate-admin {
          padding: 24px;
          background: #f5f8fb;
          min-height: 100vh;
          font-family: 'Source Sans 3', sans-serif;
          color: #1f2937;
        }
        .certificate-admin .panel {
          background: #ffffff;
          border-radius: 16px;
          padding: 20px;
          box-shadow: 0 12px 28px rgba(15, 23, 42, 0.08);
        }
        .certificate-admin .layout {
          display: grid;
          gap: 20px;
          grid-template-columns: minmax(280px, 360px) 1fr;
        }
        .certificate-admin h2 {
          font-size: 20px;
          margin: 0 0 12px;
        }
        .certificate-admin .field {
          margin-bottom: 12px;
        }
        .certificate-admin label {
          display: block;
          font-size: 12px;
          color: #6b7280;
          margin-bottom: 6px;
        }
        .certificate-admin input,
        .certificate-admin textarea,
        .certificate-admin select {
          width: 100%;
          border: 1px solid #d1d9e6;
          border-radius: 10px;
          padding: 9px 12px;
          font-size: 13px;
          font-family: 'Source Sans 3', sans-serif;
        }
        .certificate-admin textarea {
          min-height: 68px;
          resize: vertical;
        }
        .certificate-admin .row {
          display: grid;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          gap: 10px;
        }
        .certificate-admin .actions {
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }
        .certificate-admin .btn {
          border: none;
          border-radius: 10px;
          padding: 9px 14px;
          font-weight: 600;
          cursor: pointer;
        }
        .certificate-admin .btn.primary {
          background: #0aa1dc;
          color: #fff;
        }
        .certificate-admin .btn.outline {
          background: transparent;
          color: #0aa1dc;
          border: 1px solid #0aa1dc;
        }
        .certificate-admin .material-row {
          display: grid;
          grid-template-columns: 2fr 1fr 2fr auto;
          gap: 8px;
          align-items: center;
          margin-bottom: 8px;
        }
        .certificate-admin .material-row button {
          padding: 6px 10px;
        }
        .certificate-admin .preview-frame {
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          overflow: hidden;
          background: #fff;
          min-height: 900px;
        }
        .certificate-admin .preview-frame iframe {
          width: 100%;
          height: 900px;
          border: none;
        }
        .certificate-admin .status {
          font-size: 12px;
          color: #6b7280;
        }
        @media (max-width: 1024px) {
          .certificate-admin .layout {
            grid-template-columns: 1fr;
          }
          .certificate-admin .preview-frame iframe {
            height: 760px;
          }
        }
      `}</style>

      <div className="layout">
        <div className="panel">
          <h2>Certificate Admin</h2>
          <div className="field">
            <label>Nomor Sertifikat (Cari)</label>
            <div className="row">
              <input
                value={searchNumber}
                onChange={(e) => setSearchNumber(e.target.value)}
                placeholder="001/JP-SF/VI/2026"
              />
              <button className="btn primary" onClick={handleSearch}>
                Load
              </button>
            </div>
          </div>

          {isLoading && <div className="status">Memuat data...</div>}
          {isError && <div className="status">Sertifikat tidak ditemukan.</div>}

          {course && cert && (
            <>
              <div className="field">
                <label>Nama Peserta</label>
                <input value={cert.name} readOnly />
              </div>
              <div className="field">
                <label>Judul Course</label>
                <textarea value={course.title} readOnly />
              </div>
              <div className="row">
                <div className="field">
                  <label>Nomor Sertifikat (Auto)</label>
                  <input value={autoCertificateNumber} readOnly />
                </div>
                <div className="field">
                  <label>Nomor Sertifikat (Manual)</label>
                  <input
                    value={manualCertificateNumber}
                    onChange={(e) => setManualCertificateNumber(e.target.value)}
                    placeholder="Opsional"
                  />
                </div>
              </div>
              <div className="field">
                <label>Held On / Tanggal Pelaksanaan (Manual)</label>
                <textarea
                  value={eventDates}
                  onChange={(e) => setEventDates(e.target.value)}
                  placeholder="Contoh: 3, 5, dan 7 June 2026"
                />
              </div>
              <div className="row">
                <div className="field">
                  <label>Jumlah JPL</label>
                  <input
                    value={jplTotal}
                    onChange={(e) => setJplTotal(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>Keterangan *) 1 JPL (Manual)</label>
                  <input
                    value={jplNote}
                    onChange={(e) => setJplNote(e.target.value)}
                    placeholder="*) 1 JPL: 45 minutes"
                  />
                </div>
              </div>
              <div className="field">
                <label>Narasumber</label>
                <textarea
                  value={speakers}
                  onChange={(e) => setSpeakers(e.target.value)}
                />
              </div>
              <div className="row">
                <div className="field">
                  <label>Lokasi TTD</label>
                  <input
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                  />
                </div>
                <div className="field">
                  <label>Tanggal Tanda Tangan</label>
                  <input
                    type="date"
                    value={signatureDateOverride || defaultSignatureDate}
                    onChange={(e) => setSignatureDateOverride(e.target.value)}
                  />
                  <div className="status">
                    Default: akhir kelas untuk Live Teaching, tanggal unduh
                    sertifikat untuk E-Course/CBT.
                  </div>
                </div>
              </div>

              <div className="field">
                <label>Materi & Narasumber</label>
                {materials.map((row, index) => (
                  <div key={`${row.topic}-${index}`} className="material-row">
                    <input
                      value={row.topic}
                      onChange={(e) =>
                        handleUpdateMaterial(index, 'topic', e.target.value)
                      }
                      placeholder="Topik"
                    />
                    <input
                      value={row.jpl}
                      onChange={(e) =>
                        handleUpdateMaterial(index, 'jpl', e.target.value)
                      }
                      placeholder="JPL"
                    />
                    <input
                      value={row.speaker}
                      onChange={(e) =>
                        handleUpdateMaterial(index, 'speaker', e.target.value)
                      }
                      placeholder="Narasumber"
                    />
                    <button
                      className="btn outline"
                      type="button"
                      onClick={() => handleRemoveMaterial(index)}
                    >
                      Hapus
                    </button>
                  </div>
                ))}
                <button className="btn outline" onClick={handleAddMaterial}>
                  Tambah Materi
                </button>
              </div>

              <div className="field">
                <label>CBT Aktif</label>
                <select
                  value={cbtEnabled ? 'yes' : 'no'}
                  onChange={(e) => setCbtEnabled(e.target.value === 'yes')}
                >
                  <option value="yes">Ya</option>
                  <option value="no">Tidak</option>
                </select>
              </div>
              <div className="row">
                <div className="field">
                  <label>Nilai CBT</label>
                  <input
                    value={evaluationScore}
                    onChange={(e) => setEvaluationScore(e.target.value)}
                    disabled={!cbtEnabled}
                  />
                </div>
                <div className="field">
                  <label>Passing Grade</label>
                  <input
                    value={evaluationPassingGrade}
                    onChange={(e) => setEvaluationPassingGrade(e.target.value)}
                    disabled={!cbtEnabled}
                  />
                </div>
              </div>
              <div className="field">
                <label>Keputusan Lulus</label>
                <select
                  value={evaluationOverride}
                  onChange={(e) =>
                    setEvaluationOverride(e.target.value as EvaluationOverride)
                  }
                  disabled={!cbtEnabled}
                >
                  <option value="auto">Otomatis</option>
                  <option value="passed">Lulus</option>
                  <option value="failed">Tidak Lulus</option>
                </select>
              </div>

              <div className="actions">
                <button className="btn primary" onClick={handleDownload}>
                  Download PDF
                </button>
              </div>
            </>
          )}
        </div>

        <div className="panel preview-frame">
          {isPreviewLoading && (
            <div className="status" style={{ padding: 16 }}>
              Membuat preview...
            </div>
          )}
          {previewUrl && !isPreviewLoading && (
            <iframe title="certificate-preview" src={previewUrl} />
          )}
          {!previewUrl && !isPreviewLoading && (
            <div className="status" style={{ padding: 16 }}>
              Preview sertifikat akan muncul di sini.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default CertificateAdminPreview
